import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IDocumentTable, ISetValue, ISqlProcedure } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { EnvironmentService } from '@services/EnvironmentService';
import { RefresherDataSourceService } from '@services/RefresherDataSourceService';
import { GetValueForSetValue } from '@utils/managers/ChangesManager';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { TableRow } from './tableColumns';

/** Ключи ответа процедуры приводятся к виду ключей строки таблицы (`|Поле`). */
const withRowKeys = (row: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    Object.keys(row).forEach((key) => {
        result[key.startsWith('|') ? key : `|${key}`] = row[key];
    });
    return result;
};

/**
 * Строки таблицы, дополненные данными SQL-процедуры (`extendsDataSource`):
 * процедура возвращает набор, который матчится к строкам по `primaryKey` (поле
 * строки) ↔ `foreignKey` (поле ответа). Данные процедуры в документ не
 * сохраняются — это витрина поверх строк формы, поэтому наложение живёт здесь,
 * а не в значении поля.
 *
 * `refreshTimeOut > 0` включает автообновление; его надо останавливать на время
 * действий по строке (иначе меню действий закроется под обновлением) — для этого
 * возвращаются `pause`/`refresh`.
 */
export const useExtendsDataSource = <TFieldValues extends object>({
    table,
    rows,
    fields,
    formMethods,
    enabled,
}: {
    table: IDocumentTable;
    rows: TableRow[];
    fields: Record<string, IFieldElem>;
    formMethods: UseFormReturn<TFieldValues>;
    enabled: boolean;
}) => {
    const sqlProcedure = enabled ? table.extendsDataSource?.sqlProcedure : undefined;
    const [extra, setExtra] = useState<ReadonlyMap<string, Record<string, unknown>>>(new Map());
    const alive = useRef(true);
    const refresher = useMemo(
        () =>
            sqlProcedure?.refreshTimeOut
                ? new RefresherDataSourceService(sqlProcedure.refreshTimeOut, sqlProcedure.refreshTimeOut)
                : undefined,
        [sqlProcedure?.refreshTimeOut],
    );
    // параметры процедуры считаются по документу (не по строке) — их пересчёт
    // не должен тянуть за собой перезапуск загрузки на каждый рендер
    const paramsOf = useCallback(
        async (params: ISetValue[]) => {
            const result: Record<string, unknown> = {};
            for (const param of params ?? []) {
                result[param.key] = await GetValueForSetValue(param, undefined, fields, undefined, formMethods);
            }
            return JSON.stringify(result);
        },
        [fields, formMethods],
    );

    const load = useCallback(
        async (procedure: ISqlProcedure) => {
            try {
                const response = await EnvironmentService.getExtendData(procedure.name, await paramsOf(procedure.params), true);
                if (!alive.current) return;
                const byKey = new Map<string, Record<string, unknown>>();
                (response.data ?? []).forEach((item: Record<string, unknown>) => {
                    const key = item[procedure.foreignKey];
                    if (key !== undefined && key !== null) byKey.set(String(key), withRowKeys(item));
                });
                setExtra(byKey);
            } catch (error: unknown) {
                if (!alive.current) return;
                sendModalMsg({
                    message: [Array.isArray(error) ? String(error[0]) : `Не удалось получить данные «${procedure.name}».`],
                    type: 'error',
                });
            }
        },
        [paramsOf],
    );

    useEffect(() => {
        alive.current = true;
        if (!sqlProcedure) {
            setExtra((prev) => (prev.size === 0 ? prev : new Map()));
            return;
        }
        void load(sqlProcedure);
        if (refresher && sqlProcedure.refreshTimeOut > 0) {
            refresher.startMonitor(() => void load(sqlProcedure));
        }
        return () => {
            alive.current = false;
            refresher?.stop();
        };
    }, [sqlProcedure, refresher, load]);

    const extendedRows = useMemo(() => {
        if (!sqlProcedure || extra.size === 0) return rows;
        return rows.map((row) => {
            const match = extra.get(String(row[sqlProcedure.primaryKey] ?? ''));
            return match ? { ...row, ...match } : row;
        });
    }, [rows, extra, sqlProcedure]);

    return {
        rows: extendedRows,
        /** Остановить автообновление (на время открытого меню действий строки). */
        pause: useCallback(() => refresher?.stop(), [refresher]),
        /** Перечитать данные процедуры (после действия, изменившего документы). */
        refresh: useCallback(() => {
            if (sqlProcedure) void load(sqlProcedure);
        }, [load, sqlProcedure]),
    };
};
